import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RULES, type DriverState } from '@torapon/shared';
import { useNames } from '../lib/directory';
import { useDriverStatus } from '../lib/status';
import { useTicker } from '../lib/realtime';
import { STATE, ago, attention, cautions, hm } from '../lib/driverState';

type Sort = 'attention' | 'name' | 'state';
type Filter = DriverState | 'caution' | 'delivering' | null;

/** ドライバーの状況（仕様書「ドライバーの状況」）：全ドライバーの「いま」を 1 画面で */
export function StatusPage() {
  const names = useNames();
  const nav = useNavigate();
  const { rows } = useDriverStatus();
  useTicker();
  const [only, setOnly] = useState<Filter>(null);
  const [sort, setSort] = useState<Sort>('attention');
  const list = useMemo(() => {
    const r = (rows ?? []).filter((x) => !only || (only === 'caution' ? cautions(x).length > 0 : only === 'delivering' ? x.state === 'to_pickup' || x.state === 'to_drop' : x.state === only));
    const by = { attention: (a: typeof r[number], z: typeof r[number]) => attention(a) - attention(z), name: (a: typeof r[number], z: typeof r[number]) => names.driver(a.driverId).localeCompare(names.driver(z.driverId), 'ja'), state: (a: typeof r[number], z: typeof r[number]) => a.state.localeCompare(z.state) }[sort];
    return [...r].sort((a, z) => by(a, z) || names.driver(a.driverId).localeCompare(names.driver(z.driverId), 'ja'));
  }, [rows, only, sort, names]);
  const count = (s: DriverState) => (rows ?? []).filter((x) => x.state === s).length;
  const delivering = count('to_pickup') + count('to_drop');
  const chips: [string, number, Filter][] = [
    ['すべて', rows?.length ?? 0, null], ['配送中', delivering, 'delivering'], ['受付待ち', count('unaccepted'), 'unaccepted'], ['点呼待ち', count('rollcall_waiting'), 'rollcall_waiting'],
    ['待機', count('standby'), 'standby'], ['通信なし', count('no_signal'), 'no_signal'], ['注意', (rows ?? []).filter((x) => cautions(x).length).length, 'caution'], ['勤務前', count('off'), 'off'],
  ];
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      <div className="head" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div role="group" aria-label="状態で絞り込む" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {chips.map(([label, n, key]) => (
            <button key={label} className={`btn sm ${only === key ? 'pri' : ''}`} aria-pressed={only === key} onClick={() => setOnly(key)}>
              {label} <b className="num" style={{ marginLeft: 4, color: key === 'no_signal' && n ? 'var(--alert)' : undefined }}>{n}</b>
            </button>
          ))}
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>並べ替え
          <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={{ width: 'auto' }} aria-label="並べ替え">
            <option value="attention">注意が必要な順</option><option value="name">名前</option><option value="state">状態</option>
          </select></label>
      </div>
      <div className="panel">
        <table>
          <thead><tr><th>ドライバー</th><th>状態</th><th>今の行き先</th><th>残り</th><th>拘束時間</th><th>走行</th><th>端末</th><th></th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={8} className="muted">読み込み中…</td></tr>
              : list.length === 0 ? <tr><td colSpan={8} className="muted">{only ? 'この状態のドライバーはいません' : 'まだドライバーがいません'}</td></tr>
              : list.map((r) => {
                const c = cautions(r);
                const restraint = r.restraintMin ?? 0;
                return (
                  <tr key={r.driverId} onClick={() => nav(`/drivers/${r.driverId}`)} style={{ cursor: 'pointer' }} aria-label={`${names.driver(r.driverId)} の状況`}>
                    <td><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 4, background: r.color, marginRight: 8 }} /><b>{names.driver(r.driverId)}</b><small>{r.vehicle ?? '—'}</small></td>
                    <td><span className={`pill ${STATE[r.state][1]}`}>{STATE[r.state][0]}</span><small>{r.since ? ago(r.since) + 'から' : ''}</small></td>
                    <td>{r.next ? <>{r.next.kind === 'pickup' ? '積む' : '届ける'}：<b>{names.place(r.next.placeId)}</b><small className="num">{r.next.deliveryNos.join('・')}{r.next.dueAt ? `・${r.next.dueAt} 必着` : ''}</small></> : '—'}</td>
                    <td className="num">{r.remaining || '—'}</td>
                    <td className="num" style={{ color: restraint >= RULES.dailyRestraintAlertMin ? 'var(--alert)' : restraint >= RULES.dailyRestraintWarnMin ? '#7a5400' : undefined, fontWeight: restraint >= RULES.dailyRestraintWarnMin ? 800 : undefined }}>{hm(r.restraintMin)}</td>
                    <td className="num">{r.shiftId ? `${r.distanceKm} km` : '—'}</td>
                    <td>{r.device ? <>{r.device.battery != null ? `電池 ${r.device.battery}%${r.device.charging ? '（充電中）' : ''}` : ''}<small>{ago(r.device.lastSeenAt)}・{r.device.network === 'wifi' ? 'Wi-Fi' : r.device.network === 'none' ? '圏外' : 'モバイル'}{r.device.appVersion ? `・版 ${r.device.appVersion}` : ''}</small></> : <span className="muted">—</span>}
                      {c.length > 0 && <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>{c.map((x) => <span key={x} className="pill warn">{x}</span>)}</div>}</td>
                    <td onClick={(e) => e.stopPropagation()}>{r.position && <Link className="btn sm" to={`/?driver=${r.driverId}`} style={{ textDecoration: 'none' }}>地図</Link>}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
